import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Award } from "lucide-react";

const FINISHES = ["Glossy", "Matte", "Polished", "Rustic", "Lappato"];
const SIZES = ["300x300mm", "300x600mm", "600x600mm", "600x1200mm", "800x800mm", "1200x1800mm"];

export default function BuyerRFQ() {
  const [rfqs, setRfqs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", tile_size: SIZES[2], finish: FINISHES[0], quantity_boxes: 0, target_price: "", notes: "" });

  const load = async () => {
    const { data } = await api.get("/rfq");
    setRfqs(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/rfq", { ...form, quantity_boxes: Number(form.quantity_boxes), target_price: form.target_price ? Number(form.target_price) : null });
      toast.success("RFQ posted");
      setOpen(false);
      setForm({ title: "", tile_size: SIZES[2], finish: FINISHES[0], quantity_boxes: 0, target_price: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const award = async (rfqId, bidId) => {
    try {
      await api.post(`/rfq/${rfqId}/award/${bidId}`);
      toast.success("Bid awarded — order created");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="buyer-rfq">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">My RFQs</h1>
          <p className="text-muted-foreground">Post request, collect bids, award the winner.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-rfq-btn"><Plus className="w-4 h-4 mr-1" /> New RFQ</Button></DialogTrigger>
          <DialogContent data-testid="rfq-dialog">
            <DialogHeader><DialogTitle>Post a new RFQ</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="rfq-title-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Tile size</Label>
                  <Select value={form.tile_size} onValueChange={(v) => setForm({ ...form, tile_size: v })}>
                    <SelectTrigger data-testid="rfq-size-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Finish</Label>
                  <Select value={form.finish} onValueChange={(v) => setForm({ ...form, finish: v })}>
                    <SelectTrigger data-testid="rfq-finish-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{FINISHES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Quantity (boxes)</Label><Input type="number" value={form.quantity_boxes} onChange={(e) => setForm({ ...form, quantity_boxes: e.target.value })} data-testid="rfq-qty-input" /></div>
                <div className="space-y-2"><Label>Target price/box</Label><Input type="number" value={form.target_price} onChange={(e) => setForm({ ...form, target_price: e.target.value })} data-testid="rfq-target-input" /></div>
              </div>
              <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="rfq-notes-input" /></div>
              <Button onClick={submit} data-testid="rfq-submit-btn">Post RFQ</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {rfqs.map((r) => (
          <div key={r.id} className="surface-card p-6" data-testid={`buyer-rfq-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="label-overline">{r.tile_size} · {r.finish} · {r.quantity_boxes} boxes</div>
                <div className="font-display text-lg font-black mt-1">{r.title}</div>
                {r.notes && <div className="text-sm text-muted-foreground mt-1">{r.notes}</div>}
              </div>
              <Badge variant="outline" className="font-mono text-[10px]">{r.status}</Badge>
            </div>
            <div className="mt-4">
              <div className="label-overline mb-2">Bids ({r.bids?.length || 0})</div>
              {(!r.bids || r.bids.length === 0) ? (
                <div className="text-sm text-muted-foreground">No bids yet.</div>
              ) : (
                <div className="space-y-2">
                  {[...r.bids].sort((a, b) => a.price_per_box - b.price_per_box).map((b) => (
                    <div key={b.id} className="flex items-center justify-between border border-border rounded-md p-3">
                      <div className="text-sm">
                        <div className="font-semibold">{b.vendor_name}</div>
                        <div className="text-xs text-muted-foreground font-mono">₹{b.price_per_box}/box · {b.delivery_days} days</div>
                      </div>
                      {r.status === "OPEN" && (
                        <Button size="sm" onClick={() => award(r.id, b.id)} data-testid={`award-${b.id}`}><Award className="w-3 h-3 mr-1" /> Award</Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {rfqs.length === 0 && <div className="surface-card p-8 text-center text-sm text-muted-foreground" data-testid="buyer-rfq-empty">No RFQs yet. Post one above.</div>}
      </div>
    </div>
  );
}
